import { GetAlergyIntoleranceByOrganization } from "@/services/fhir/alergyintolerance/getAlergyIntoleranceByOrganization";
import { GetConditionByOrganization } from "@/services/fhir/condition/GetConditionByOrganization";
import { GetMedicationRequestByOrganization } from "@/services/fhir/medicationrequest/GetMedicationRequestByOrganization";
import { GetServiceRequestByOrganization } from "@/services/fhir/servicerequest/GetServiceRequestByOrganization";

export interface InsightOrgQuery {
  organizationId: string;
  startDate?: string;
  endDate?: string;
}

function resourcesOfType<T extends fhir5.Resource>(
  bundle: fhir5.Bundle | undefined,
  resourceType: T["resourceType"],
): T[] {
  if (!bundle?.entry) return [];
  return bundle.entry
    .map((entry) => entry.resource)
    .filter((resource): resource is T => resource?.resourceType === resourceType);
}

export async function fetchInsightConditions(
  query: InsightOrgQuery,
): Promise<fhir5.Condition[]> {
  const base = {
    organizationId: query.organizationId,
    startDate: query.startDate,
    endDate: query.endDate,
    includeEncounter: false,
    includePatient: false as const,
  };
  const countResponse = await GetConditionByOrganization({
    ...base,
    summary: true,
    count: 0,
  });
  const total = countResponse.total || 0;
  const bundle = await GetConditionByOrganization({
    ...base,
    summary: false,
    count: total > 0 ? total : 1000,
  });
  return resourcesOfType<fhir5.Condition>(bundle, "Condition");
}

export async function fetchInsightMedicationRequests(
  query: InsightOrgQuery,
): Promise<fhir5.MedicationRequest[]> {
  const base = {
    organizationId: query.organizationId,
    startDate: query.startDate,
    endDate: query.endDate,
    includeEncounter: false,
    includePatient: false as const,
  };
  const countResponse = await GetMedicationRequestByOrganization({
    ...base,
    summary: true,
    count: 0,
  });
  const total = countResponse.total || 0;
  const bundle = await GetMedicationRequestByOrganization({
    ...base,
    summary: false,
    count: total > 0 ? total : 1000,
  });
  return resourcesOfType<fhir5.MedicationRequest>(bundle, "MedicationRequest");
}

export async function fetchInsightAllergies(
  query: InsightOrgQuery,
): Promise<fhir5.AllergyIntolerance[]> {
  const base = {
    organizationId: query.organizationId,
    startDate: query.startDate,
    endDate: query.endDate,
    includeEncounter: true,
    includePatient: false as const,
  };
  const countResponse = await GetAlergyIntoleranceByOrganization({
    ...base,
    summary: true,
    count: 0,
  });
  const total = countResponse.total || 0;
  const bundle = await GetAlergyIntoleranceByOrganization({
    ...base,
    summary: false,
    count: total > 0 ? total : 1000,
  });
  return resourcesOfType<fhir5.AllergyIntolerance>(bundle, "AllergyIntolerance");
}

export async function fetchInsightServiceRequests(
  query: InsightOrgQuery,
): Promise<fhir5.ServiceRequest[]> {
  const base = {
    organizationId: query.organizationId,
    startDate: query.startDate,
    endDate: query.endDate,
    includeEncounter: true,
    includePatient: false as const,
  };
  const countResponse = await GetServiceRequestByOrganization({
    ...base,
    summary: true,
    count: 0,
  });
  const total = countResponse.total || 0;
  const bundle = await GetServiceRequestByOrganization({
    ...base,
    summary: false,
    count: total > 0 ? total : 1000,
  });
  return resourcesOfType<fhir5.ServiceRequest>(bundle, "ServiceRequest");
}
